import { useEffect, useRef, useState } from 'react'
import { api, type InquiryCallback as Callback } from '../../api/client'
import { useSession } from '../../api/session'

const message = (e: unknown) => e instanceof Error ? e.message : 'Could not update inquiry callback readiness'

export default function InquiryCallback({companyId, inquiryId}: {companyId: string; inquiryId: string}) {
  const { user } = useSession()
  const [open, setOpen] = useState(false)
  const [saved, setSaved] = useState<Callback | null>(null)
  const [review, setReview] = useState<{ready: boolean; revision: number} | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const pending = useRef(false)
  const generation = useRef(0)
  useEffect(() => {
    if (!open) return
    let live = true
    let timer: number | undefined
    const reload = async () => {
      const version = generation.current
      try {
        if (pending.current) return
        const result = await api.inquiryCallback(companyId, inquiryId)
        if (live && version === generation.current) { setSaved(result); setLoadError('') }
      } catch (e) { if (live && version === generation.current) setLoadError(message(e)) }
      finally { if (live) timer = window.setTimeout(reload, 5000) }
    }
    void reload()
    return () => { live = false; window.clearTimeout(timer) }
  }, [companyId, inquiryId, open, refresh])
  return <details onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>Callback readiness for this inquiry</summary>
    {loadError && <p className="notice crit" role="alert">{loadError} Retrying automatically.</p>}
    {error && <p className="notice crit" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!saved && open && !loadError && <p className="muted">Loading callback readiness…</p>}
    {saved && <>
      <p><strong>{saved.ready ? 'Ready for a callback' : 'Not ready for a callback'}</strong></p>
      <p>{saved.reference ? <>Inquiry callback reference: <strong>{saved.reference}</strong></> : 'No callback reference has been created for this inquiry.'}</p>
      {!saved.voice_supported && <p className="notice">{saved.activation_reason || 'This inquiry is not currently available for callbacks.'}</p>}
      {saved.voice_supported && saved.activation_reason && <p className="notice">{saved.activation_reason}</p>}
      <p className="muted">Ready means this inquiry is prepared for a callback. It does not mean a call has connected or completed.</p>
      <p className="muted">Readiness and the reference belong to this inquiry. A company's existing callback setup does not make another inquiry ready.</p>
      {saved.actor && <p className="muted">Last changed by {saved.actor}{saved.updated_at && ` · ${new Date(saved.updated_at).toLocaleString()}`}{saved.reason && ` · ${saved.reason}`}</p>}
      {user?.role !== 'admin' && <p className="muted">Only an administrator can change callback readiness.</p>}
      {user?.role === 'admin' && !review && (saved.voice_supported || saved.ready) && <button className="btn secondary small" disabled={busy} onClick={() => { setReview({ready: !saved.ready, revision: saved.revision}); setReason(''); setNotice('') }}>{saved.ready ? 'Pause inquiry callbacks' : 'Activate inquiry callbacks'}</button>}
    </>}
    {user?.role === 'admin' && review && <form className="confirm" style={{marginTop: '0.75rem'}} onSubmit={async event => {
      event.preventDefault()
      if (pending.current || !reason.trim() || (review.ready && !saved?.voice_supported)) return
      pending.current = true; setBusy(true); setError(''); generation.current += 1
      try {
        const result = await api.setInquiryCallback(companyId, inquiryId, {ready: review.ready, expected_revision: review.revision, reason: reason.trim()})
        setSaved(result); setLoadError(''); setReview(null); setReason(''); setNotice(result.ready ? 'Inquiry callback readiness activated.' : 'Inquiry callback readiness paused.')
      } catch (e) { setError(message(e)) }
      finally { generation.current += 1; pending.current = false; setBusy(false) }
    }}>
      <strong>{review.ready ? 'Activate callbacks for this inquiry' : 'Pause callbacks for this inquiry'}</strong>
      <p>This changes the selected inquiry's readiness. It does not send an email or place a call.</p>
      {review.revision !== saved?.revision && <p className="notice">Readiness changed while you were reviewing it. Reload before saving.</p>}
      <label className="field">Reason for change<input className="input" required maxLength={2000} disabled={busy} value={reason} onChange={event => setReason(event.target.value)} /></label>
      <div className="action-group"><button className="btn small" disabled={busy || !reason.trim() || review.revision !== saved?.revision || (review.ready && !saved?.voice_supported)}>Save callback readiness</button><button type="button" className="btn secondary small" disabled={busy} onClick={() => { setReview(null); setReason('') }}>Cancel</button></div>
    </form>}
    {open && <button className="btn secondary small" style={{marginTop: '0.75rem'}} disabled={busy} onClick={() => { setReview(null); setReason(''); setError(''); setSaved(null); setRefresh(value => value + 1) }}>Reload callback readiness</button>}
  </details>
}
